import React, { useRef, useMemo, useState, Fragment } from "react";
import { PolarArea } from "react-chartjs-2";
import { Chart, RadialLinearScale, ArcElement, Tooltip, Legend } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import html2canvas from "html2canvas";
import { ArrowDownTrayIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Dialog, Transition } from "@headlessui/react";

Chart.register(RadialLinearScale, ArcElement, Tooltip, Legend, ChartDataLabels);

const fallbackColors = [
    "#aeea84", "#fcd39e", "#a8a1e9", "#ff6384", "#36a2eb", "#ffce56", "#b8e994",
    "#f6e58d", "#686de0", "#303952", "#f5cd79", "#574b90", "#f3a683", "#574b90"
];

// Utility function removed - we now show full text labels without truncation

function downloadChartAsPng(chartRef, title = "chart") {
    const chartInstance = chartRef.current;
    if (!chartInstance) return;
    const canvas = chartInstance.canvas;
    if (!canvas) return;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png", 1.0);
    link.download = `${title.replace(/\s+/g, "_")}.png`;
    link.click();
}

// Full-screen modal chart for proper export
function ChartExportModal({ isOpen, closeModal, codes, title }) {
    const modalChartRef = useRef();

    const { labels, values, colors } = useMemo(() => {
        if (!Array.isArray(codes) || codes.length === 0) {
            return { labels: [], values: [], colors: [] };
        }
        
        try {
            // No truncation for modal version - show full labels
            const labels = codes.map(code => code?.name || 'Unknown');
            const colors = codes.map((code, idx) => code?.color || fallbackColors[idx % fallbackColors.length]);
            const values = codes.map(code => Array.isArray(code?.excerpts) ? code.excerpts.length : 0);
            return { labels, values, colors };
        } catch (error) {
            console.error('Error processing modal chart data:', error);
            return { labels: [], values: [], colors: [] };
        }
    }, [codes]);

    const modalData = {
        labels,
        datasets: [{
            label: "",
            data: values,
            backgroundColor: colors
        }]
    };

    const modalOptions = {
        responsive: false,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 120,
                bottom: 280,
                left: 120,
                right: 120
            }
        },
        scales: {
            r: {
                pointLabels: {
                    display: true,
                    centerPointLabels: true,
                    font: { size: 16, weight: "normal" },
                    padding: 80, // Maximum padding for very long labels
                },
                ticks: {
                    display: false
                },
                beginAtZero: true
            }
        },
        plugins: {
            legend: { display: false },
            title: { display: false },
            datalabels: {
                display: true,
                color: "#000",
                font: { weight: "normal", size: 10 },
                formatter: (v) => (v > 0 ? v : ""),
                anchor: "middle",
                align: "middle",
                offset: 0,
            }
        }
    };

    const handleModalExport = () => {
        downloadChartAsPng(modalChartRef, title);
    };

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={closeModal}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all w-full max-w-7xl">
                                <div className="bg-white px-6 py-6">
                                    {/* Modal Header */}
                                    <div className="flex items-center justify-between mb-6">
                                        <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={handleModalExport}
                                                className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
                                            >
                                                <ArrowDownTrayIcon className="w-5 h-5" /> PNG
                                            </button>
                                            <button
                                                onClick={closeModal}
                                                className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200"
                                            >
                                                <XMarkIcon className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {/* Full-width Chart */}
                                    <div className="flex justify-center">
                                        <div
                                            style={{
                                                width: "1100px",
                                                height: "1350px",
                                            }}
                                        >
                                            <PolarArea
                                                ref={modalChartRef}
                                                data={modalData}
                                                options={modalOptions}
                                                width={1100}
                                                height={1350}
                                                plugins={[ChartDataLabels, whiteBackgroundPlugin]}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}

const whiteBackgroundPlugin = {
    id: 'custom_canvas_background_color',
    beforeDraw: (chart) => {
        const { ctx } = chart;
        ctx.save();
        ctx.globalCompositeOperation = 'destination-over';
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, chart.width, chart.height);
        ctx.restore();
    }
}

export default function PolarAreaCenterLabelsChart({
    codes = [],
    files = [],
    mode = "codes-per-interview",
    title = ""
}) {
    const chartRef = useRef();
    const [showExportModal, setShowExportModal] = useState(false);

    const { labels, values, colors } = useMemo(() => {
        if (!Array.isArray(codes) || codes.length === 0) {
            return { labels: [], values: [], colors: [] };
        }
        
        try {
            const labels = codes.map(code => code?.name || 'Unknown');
            const colors = codes.map((code, idx) => code?.color || fallbackColors[idx % fallbackColors.length]);
            const values = codes.map(code => Array.isArray(code?.excerpts) ? code.excerpts.length : 0);
            return { labels, values, colors };
        } catch (error) {
            console.error('Error processing chart data:', error);
            return { labels: [], values: [], colors: [] };
        }
    }, [codes]);

    const data = {
        labels,
        datasets: [{
            label: "",
            data: values,
            backgroundColor: colors
        }]
    };

    const options = {
        responsive: false,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 80,
                bottom: 80,
                left: 60,
                right: 60
            }
        },
        scales: {
            r: {
                pointLabels: {
                    display: true,
                    centerPointLabels: true,
                    font: { size: 11, weight: "normal" },
                    padding: 35, // increased padding for long labels
                },
                ticks: {
                    display: false // hides the circular ticks
                },
                beginAtZero: true
            }
        },
        plugins: {
            legend: { display: false },
            title: { display: false },
            datalabels: {
                display: true,
                color: "#000",
                font: { weight: "thin", size: 12 },
                formatter: (v) => (v > 0 ? v : ""),
                anchor: "middle",
                align: "middle",
                offset: 0,
                // Optional: adjust position dynamically if needed
            }
        }
    };

    // Don't render if there's no valid data
    if (!Array.isArray(codes) || codes.length === 0 || labels.length === 0) {
        return (
            <div className="space-y-4">
                <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
                    <button
                        disabled
                        className="flex items-center gap-2 px-3 py-1 bg-gray-300 text-gray-500 rounded cursor-not-allowed"
                    >
                        <ArrowDownTrayIcon className="w-5 h-5" /> PNG
                    </button>
                </div>
                <div className="border border-gray-300 shadow p-4 flex flex-col w-full justify-center items-center">
                    <div className="text-gray-500 italic text-center py-8">
                        No data available to display
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
                <button
                    onClick={() => setShowExportModal(true)}
                    className="flex items-center gap-2 px-3 py-1 bg-black text-white rounded hover:bg-gray-800"
                >
                    <ArrowDownTrayIcon className="w-5 h-5" /> PNG
                </button>
            </div>
            <div>
                <div className="border border-gray-300 shadow p-4 flex flex-col w-full justify-center items-center">
                    <div
                        className="relative w-full flex justify-center"
                        style={{
                            maxWidth: "100%",
                        }}
                    >
                        <PolarArea
                            ref={chartRef}
                            data={data}
                            options={options}
                            height={600}
                            width={600}
                            plugins={[ChartDataLabels, whiteBackgroundPlugin]}
                            style={{ width: "600px", height: "600px" }}
                        />
                    </div>
                </div>
            </div>
            
            {/* Export Modal */}
            <ChartExportModal 
                isOpen={showExportModal}
                closeModal={() => setShowExportModal(false)}
                codes={codes}
                title={title}
            />
        </div>
    );
}
